import axios from 'axios';
import mixpanel from 'mixpanel-browser';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { gtag_report_conversion } from '../utils';

import {
  GETTING_CSRF_TOKEN,
  GETTING_USER_BY_ID,
  GETTING_USER_BY_EMAIL,
  SET_CSRF_TOKEN,
  LOGIN_USER,
  LOGGED_IN_USER,
  PATCH_UPDATE_USER,
  SET_USER_INFO,
  LOGGED_IN_ERROR,
  CLEAR_LOGGED_IN_ERROR_MESSAGE,
  SIGNING_UP_USER,
  SIGN_UP_USER,
  EMAIL_RATES_SENT,
  SENDING_EMAIL_RATES,
  SENDING_RATES_ERROR,
  SIGN_UP_ERROR,
  CLEAR_SIGN_UP_ERROR_MESSAGE,
  LOGOUT_USER,
  FORGET_PASSWORD,
  RESET_PASSWORD,
  HANDLE_AUTH_CALLBACK,
  TOKEN,
  GOOGLE_LOGGED_IN_USER,
  FACEBOOK_LOGGED_IN_USER,
} from './actionTypes';

import { API_ENDPOINT } from '../constants';

import { MIXPANEL_DEVICE_USER_LINK } from '../MixpanelDeviceUserLink/mixpanel_device_user_link';

// Getting CsrfToken
export const getCsrfToken = createAsyncThunk(GETTING_CSRF_TOKEN, async (_, methods) => {
  const { dispatch } = methods;

  let response;

  try {
    const res = await axios.get(`${API_ENDPOINT}/csrf-token`);

    response = res?.data;
  } catch (error) {
    console.error(error);
  }

  dispatch({ type: SET_CSRF_TOKEN, payload: response });
});

//Get User By Id
export const getUserById = createAsyncThunk(GETTING_USER_BY_ID, async ({ id, token }, methods) => {
  const { dispatch } = methods;

  let response;

  try {
    const res = await axios.get(`${API_ENDPOINT}/users/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    response = res?.data;
  } catch (error) {
    console.error(error);
  }

  dispatch({ type: SET_USER_INFO, payload: response });
});

export const getUserByEmail = createAsyncThunk(
  GETTING_USER_BY_EMAIL,
  async ({ email, token }, methods) => {
    const { dispatch } = methods;

    let response;

    try {
      const res = await axios.post(`${API_ENDPOINT}/users/${email}`, {
        email,
        // headers: {
        //   Authorization: `Bearer ${token}`,
        // },
      });

      response = res?.data;
    } catch (error) {
      console.error(error);
    }

    if (response?.user?.id) {
      // Extract this block and Line 281 to same helper function
      const { user } = response;
      const anonymousDeviceId = mixpanel.get_distinct_id();
      const isUserAliasedAlready = localStorage.getItem(user.id);
      if (!isUserAliasedAlready) {
        if (
          MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV][
            anonymousDeviceId
          ] === user.id
        ) {
          mixpanel.alias(user.id);
          localStorage.setItem(user.id, anonymousDeviceId);
        } else {
          const userDeviceLocated = Object.keys(
            MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV],
          ).find((deviceId) => {
            return (
              MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV][
                deviceId
              ] === user.id
            );
          });

          if (userDeviceLocated) {
            mixpanel.alias(user.id, userDeviceLocated);
            localStorage.setItem(user.id, anonymousDeviceId);
          }
        }
      }

      mixpanel.identify(user.id);
    }

    dispatch({ type: SET_USER_INFO, payload: response });
  },
);

export const signingUpUser = (dispatch) => {
  dispatch({ type: SIGNING_UP_USER });
};

export const signUpUser = createAsyncThunk(
  SIGNING_UP_USER,
  async ({ user, csrfToken }, methods) => {
    const { dispatch } = methods;

    let response;

    try {
      response = await axios.post(
        `${API_ENDPOINT}/signup`,
        {
          ...user,
        },
        // {
        //   headers: {
        //     'X-CSRF-Token': csrfToken,
        //   },
        // },
      );
    } catch (error) {
      console.error(error);
    }
    const payload = response?.data;
    const userId = payload?.user?.id;
    const { jwt } = payload || {};

    if (userId) {
      mixpanel.alias(userId);
      mixpanel.identify(userId);
      // mixpanel.register({ $device_id: userId, user_or_device_id: userId });
    }

    if (jwt) {
      const decoded = JSON.parse(atob(jwt.split('.')[1]));

      if (decoded.first_time_sign_up) {
        const issuedAt = new Date(decoded.iat * 1000);
        const now = new Date();

        const withinLastHour = now.getTime() - issuedAt.getTime() <= 60 * 60 * 1000;

        if (withinLastHour) {
          gtag_report_conversion('/signup');
        }
      }
    }

    dispatch({ type: SIGN_UP_USER, payload });
  },
);

export const signUpUserWithEmailForRates = createAsyncThunk(
  SIGNING_UP_USER,
  async ({ user, csrfToken, financingType, vehOrPropertyProps, usState }, methods) => {
    const { dispatch } = methods;

    let response;

    try {
      response = await axios.post(
        `${API_ENDPOINT}/signup`,
        {
          ...user,
          displayInfoSummary: vehOrPropertyProps[financingType],
          usState,
          financingType,
        },
        // {
        //   headers: {
        //     'X-CSRF-Token': csrfToken,
        //   },
        // },
      );
      const payload = response?.data;
      const userId = payload?.user?.id;

      if (userId) {
        mixpanel.alias(userId);
        mixpanel.identify(userId);
        // mixpanel.register({ $device_id: userId, user_or_device_id: userId });
      }

      dispatch({ type: SIGN_UP_USER, payload: response?.data });
    } catch (error) {
      const ERROR_CREATING_USER = 'ERROR CREATING USER';
      dispatch({ type: SIGN_UP_ERROR, payload: ERROR_CREATING_USER });
      console.error(error);
    }
  },
);

export const sendEmailRates = createAsyncThunk(
  SENDING_EMAIL_RATES,
  async ({ user, csrfToken, financingType, vehOrPropertyProps, usState, tokenValue }, methods) => {
    const { dispatch } = methods;

    if (!user || !user.id) {
      return;
    }

    let response;

    try {
      response = await axios.post(
        `${API_ENDPOINT}/users/${user.id}/send_email_rates`,
        {
          ...user,
          displayInfoSummary: vehOrPropertyProps[financingType],
          usState,
          financingType,
          model: 'user',
        },
        {
          headers: {
            Authorization: `Bearer ${tokenValue}`,
          },
        },
        // {
        //   headers: {
        //     'X-CSRF-Token': csrfToken,
        //   },
        // },
      );
      dispatch({ type: EMAIL_RATES_SENT, payload: EMAIL_RATES_SENT });
    } catch (error) {
      const ERROR_SENDING_RATES = 'ERROR SENDING RATES';
      dispatch({ type: SENDING_RATES_ERROR, payload: ERROR_SENDING_RATES });
      console.error(error);
    }
  },
);

export const clearSignUpUserErrorMessage = (dispatch) => {
  dispatch({ type: CLEAR_SIGN_UP_ERROR_MESSAGE });
};

export const loggingInUser = (dispatch) => {
  dispatch({ type: LOGIN_USER });
};

export const loggedInUser = createAsyncThunk(LOGIN_USER, async ({ user, csrfToken }, methods) => {
  const { dispatch } = methods;

  let response;

  try {
    response = await axios.post(
      `${API_ENDPOINT}/login`,
      {
        ...user,
      },
      //   {
      //     headers: {
      //       'X-CSRF-Token': csrfToken,
      //     },
      //   },
    );
    const payload = response?.data;
    const userId = payload?.user?.id;

    if (userId) {
      const anonymousDeviceId = mixpanel.get_distinct_id();
      const isUserAliasedAlready = localStorage.getItem(user.id);
      if (!isUserAliasedAlready) {
        if (
          MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV][
            anonymousDeviceId
          ] === userId
        ) {
          mixpanel.alias(userId);
          localStorage.setItem(user.id, anonymousDeviceId);
        } else {
          const userDeviceLocated = Object.keys(
            MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV],
          ).find((deviceId) => {
            return (
              MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV][
                deviceId
              ] === userId
            );
          });

          if (userDeviceLocated) {
            mixpanel.alias(userId, userDeviceLocated);
            localStorage.setItem(user.id, anonymousDeviceId);
          }
        }
      }

      mixpanel.identify(userId);
    }

    dispatch({ type: LOGGED_IN_USER, payload: response?.data });
  } catch (error) {
    dispatch({ type: LOGGED_IN_ERROR, payload: error?.response?.data });
    console.error(error);
  }
});

export const updatingUserInfo = (dispatch) => {
  dispatch({ type: PATCH_UPDATE_USER });
};

export const updateUserInfo = createAsyncThunk(
  PATCH_UPDATE_USER,
  async ({ id, userInfo, token }, methods) => {
    const { dispatch } = methods;

    let response;

    try {
      response = await axios.patch(
        `${API_ENDPOINT}/users/${id}`,
        {
          ...userInfo,
          model: 'user',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch (error) {
      console.error(error);
    }

    dispatch({ type: SET_USER_INFO, payload: response?.data });
  },
);

export const setUserInfo = (dispatch, userInfo) => {
  dispatch({ type: SET_USER_INFO, payload: userInfo });
};

export const clearLoggedInErrorMessage = (dispatch) => {
  dispatch({ type: CLEAR_LOGGED_IN_ERROR_MESSAGE });
};

// LOGOUT USER
export const loggedOutUser = createAsyncThunk(LOGOUT_USER, async ({ user }, methods) => {
  const { dispatch } = methods;

  let response;

  try {
    localStorage.removeItem(TOKEN);
    response = await axios.post(`${API_ENDPOINT}/logout`, { user });
  } catch (error) {
    console.error(error);
  }

  dispatch({ type: LOGOUT_USER, payload: response?.data });
});

// in actions.js
export const forgetPassword = createAsyncThunk(
  FORGET_PASSWORD,
  async ({ email, csrfToken }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_ENDPOINT}/forgot_password`,
        { email },
        // {
        //   headers: {
        //     'X-CSRF-Token': csrfToken,
        //   },
        // },
      );
      return response.data;
    } catch (error) {
      console.error('error getting email', error);
      return rejectWithValue(error.response?.data?.message || 'Something went wrong');
    }
  },
);

export const post_resetPassword = createAsyncThunk(
  RESET_PASSWORD,
  async ({ token, newPassword, csrfToken }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.patch(
        `${API_ENDPOINT}/reset_password`,
        {
          password: newPassword,
          confirmPassword: newPassword,
          token,
        },
        // {
        //   headers: {
        //     'X-CSRF-Token': csrfToken,
        //   },
        // },
      );

      const responseData = response?.data;

      if (responseData?.user) {
        const userDeviceLocated = Object.keys(
          MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV],
        ).find((deviceId) => {
          return (
            MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV][deviceId] ===
            responseData.user?.id
          );
        });

        if (userDeviceLocated) {
          mixpanel.alias(responseData.user?.id, userDeviceLocated);
          localStorage.setItem(responseData.user?.id, userDeviceLocated);
        }

        mixpanel.identify(responseData?.user.id);
      }

      dispatch({ type: LOGGED_IN_USER, payload: responseData });
    } catch (error) {
      console.error('error resetting password', error);
      return rejectWithValue(error.response?.data || { message: 'Something went wrong' });
    }
  },
);

export const handleAuthCallbacks = createAsyncThunk(
  HANDLE_AUTH_CALLBACK,
  async (token, { dispatch, rejectWithValue }) => {
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));

      const provider = decoded.authSource || decoded.iss;

      localStorage.setItem(TOKEN, token);
      localStorage.setItem('provider', provider);

      if (provider === 'google') {
        dispatch({ type: GOOGLE_LOGGED_IN_USER, payload: decoded });
      }
      if (provider === 'facebook') {
        dispatch({ type: FACEBOOK_LOGGED_IN_USER, payload: decoded });
      }

      if (decoded?.id) {
        if (decoded.auth_signup) {
          mixpanel.alias(decoded.id);
        }

        const userDeviceLocated = Object.keys(
          MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV],
        ).find((deviceId) => {
          return (
            MIXPANEL_DEVICE_USER_LINK[process.env.REACT_APP_ENV][process.env.NODE_ENV][deviceId] ===
            decoded.id
          );
        });

        if (userDeviceLocated) {
          mixpanel.alias(decoded.id, userDeviceLocated);
          localStorage.setItem(decoded.id, userDeviceLocated);
        }

        mixpanel.identify(decoded.id);
      }

      return decoded;
    } catch (error) {
      console.error('Auth token error', error);
      return rejectWithValue({ message: 'Invalid social token' });
    }
  },
);
